'use client';

import { useState, useEffect } from 'react';
import SignalCard from './SignalCard';
import Icon from '@/components/ui/AppIcon';

interface Signal {
  id: string;
  pair: string;
  type: 'BUY' | 'SELL';
  entryPrice: number;
  targetPrice: number;
  stopLoss: number;
  confidence: number;
  timeframe: string;
  status: 'ACTIVE' | 'COMPLETED' | 'STOPPED';
  profit?: number;
  timestamp: string;
  isLocked?: boolean; // Add locked status for premium signals
  requiresSubscription?: boolean; // Alternative naming
}

interface SignalsListProps {
  signals: Signal[];
  filters: {
    status: string;
    type: string;
    timeframe: string;
    sortBy: string;
  };
}

const SignalsList = ({ signals, filters }: SignalsListProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filteredSignals, setFilteredSignals] = useState<Signal[]>(signals);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isHydrated) {
      let filtered = [...signals];

      if (filters.status !== 'ALL') {
        filtered = filtered.filter((signal) => signal.status === filters.status);
      }

      if (filters.type !== 'ALL') {
        filtered = filtered.filter((signal) => signal.type === filters.type);
      }

      if (filters.timeframe !== 'ALL') {
        filtered = filtered.filter((signal) => signal.timeframe === filters.timeframe);
      }

      switch (filters.sortBy) {
        case 'OLDEST':
          filtered.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          break;
        case 'CONFIDENCE':
          filtered.sort((a, b) => b.confidence - a.confidence);
          break;
        case 'PROFIT':
          filtered.sort((a, b) => (b.profit || 0) - (a.profit || 0));
          break;
        default:
          filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      }

      setFilteredSignals(filtered);
    }
  }, [signals, filters, isHydrated]);

  if (!isHydrated) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-6">
            <div className="animate-pulse">
              <div className="h-6 bg-muted rounded w-24 mb-4"></div>
              <div className="h-4 bg-muted rounded w-32 mb-2"></div>
              <div className="h-4 bg-muted rounded w-40"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (filteredSignals.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center">
        <Icon name="MagnifyingGlassIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-heading font-bold text-foreground mb-2">No Signals Found</h3>
        <p className="text-sm text-muted-foreground">Try adjusting your filters to see more results</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
      {filteredSignals.map((signal) => (
        <SignalCard key={signal.id} signal={signal} />
      ))}
    </div>
  );
};

export default SignalsList;